import { Fragment, useRef } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { BookmarkCheck } from "@acto/web-common/components";
import {
  Card,
  CardHeadline,
  Header,
  HoverRowList,
  MetaLabel,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { PinnedCaption } from "@acto/taskpane/field-sales-2/components/primitives/PinnedCaption";
import { GlassButton } from "@acto/taskpane/field-sales-2/components/primitives/GlassButton";
import { Toast } from "@acto/taskpane/field-sales-2/components/primitives/Toast";
import {
  DrillBackButton,
  OverlayBody,
  OverlayCard,
  OverlayFooter,
  OverlayFrame,
  ScrollArea,
  ScrollEndProbe,
  useScrolledToEnd,
} from "@acto/taskpane/field-sales-2/components/patterns/drilldown";
import { MEETING_PREP_ROW_PADDING_BLOCK } from "@acto/taskpane/field-sales-2/components/patterns/genui-meeting-prep/rowPrimitives";
import type { HideableProduct } from "@acto/taskpane/field-sales-2/utils/productHide";
import { SelectableProductRow } from "./SelectableProductRow";
import { HiddenProductsDisclosure, type HiddenProductView } from "./HiddenProductsDisclosure";

const FixedHeader = styled(Header)(({ theme }) => ({
  flexShrink: 0,
  paddingInline: theme.fieldSalesTwo.spacing[16],
  paddingBottom: theme.fieldSalesTwo.spacing[4],
}));

const ScrollContent = styled("div")({ position: "relative" });

const CaptionFade = styled(PinnedCaption)({ height: 0 });

const Intro = styled("div")(({ theme }) => ({
  paddingInline: theme.fieldSalesTwo.spacing[16],
  paddingBottom: theme.fieldSalesTwo.spacing[24],
  '&[data-before-hidden="true"]': { paddingBottom: theme.fieldSalesTwo.spacing[4] },
}));

const HiddenSection = styled("div")(({ theme }) => ({
  paddingInline: theme.fieldSalesTwo.spacing[16],
  paddingBottom: theme.fieldSalesTwo.spacing[12],
}));

const ListCard = styled(Card)(({ theme }) => {
  const rowPadding = theme.fieldSalesTwo.spacing[MEETING_PREP_ROW_PADDING_BLOCK];
  return {
    padding: `calc(${theme.fieldSalesTwo.spacing[24]} - ${rowPadding}) ${theme.fieldSalesTwo.spacing[20]} calc(${theme.fieldSalesTwo.spacing[32]} - ${rowPadding})`,
  };
});

const Rows = styled(HoverRowList)({ width: "100%" });

const FooterSlot = styled("div")({ display: "flex", justifyContent: "center" });

const BackButton = styled(GlassButton)(({ theme }) => ({
  width: "100%",
  height: theme.fieldSalesTwo.spacing[50],
  gap: theme.fieldSalesTwo.spacing[12],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["140"],
  color: "rgba(19,19,19,0.80)",
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.neutral[200]}`,
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  boxShadow: "none",
}));

export type PickerReturn = "talkingPoint" | "drill";

interface AdjustProductsCardProps {
  talkingPointLabel: string;
  selectable: HideableProduct[];
  hidden: HiddenProductView[];
  selectedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onBack: () => void;
  returnsTo?: PickerReturn;
  onRestore: (id: string) => void;
  restoringIds?: ReadonlySet<string>;
  confirmation?: string | null;
  onConfirmationClose?: () => void;
}

export function AdjustProductsCard({
  talkingPointLabel,
  selectable,
  hidden,
  selectedIds,
  onToggle,
  onBack,
  returnsTo = "talkingPoint",
  onRestore,
  restoringIds,
  confirmation,
  onConfirmationClose,
}: AdjustProductsCardProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.productHide");

  const hasSelection = selectable.some((product) => selectedIds.has(product.id));
  const scrollRef = useRef<HTMLDivElement>(null);
  const { probeRef, atEnd } = useScrolledToEnd(scrollRef);

  return (
    <OverlayFrame>
      <OverlayCard bleed data-at-end={atEnd}>
        <OverlayBody>
          <FixedHeader>
            <MetaLabel>{t("pickerEyebrow", { label: talkingPointLabel })}</MetaLabel>
          </FixedHeader>
          <ScrollArea ref={scrollRef}>
            <ScrollContent>
              <CaptionFade scrollRef={scrollRef} />
              <Intro data-before-hidden={hidden.length > 0}>
                <CardHeadline>{t("pickerQuestion")}</CardHeadline>
              </Intro>
              {hidden.length > 0 && (
                <HiddenSection>
                  <HiddenProductsDisclosure
                    products={hidden}
                    onRestore={onRestore}
                    restoringIds={restoringIds}
                    triggerScrollRef={scrollRef}
                    leadingDivider
                  />
                </HiddenSection>
              )}
              {selectable.length > 0 && (
                <ListCard nested>
                  <Rows role="list">
                    {selectable.map((product, index) => (
                      <Fragment key={product.id}>
                        <SelectableProductRow
                          name={product.name}
                          valueLabel={product.valueLabel}
                          checked={selectedIds.has(product.id)}
                          onCheckedChange={() => onToggle(product.id)}
                        />
                        {index < selectable.length - 1 && <Divider />}
                      </Fragment>
                    ))}
                  </Rows>
                </ListCard>
              )}
            </ScrollContent>
            <ScrollEndProbe ref={probeRef} aria-hidden="true" />
          </ScrollArea>
          {!hasSelection && (
            <OverlayFooter>
              <FooterSlot>
                {confirmation ? (
                  <Toast
                    open
                    message={confirmation}
                    onClose={() => onConfirmationClose?.()}
                    icon={<BookmarkCheck aria-hidden="true" />}
                    tintMessage
                  />
                ) : returnsTo === "drill" ? (
                  <DrillBackButton as="button" variant="action" type="button" onClick={onBack}>
                    {t("backToDrill")}
                  </DrillBackButton>
                ) : (
                  <BackButton as="button" type="button" onClick={onBack}>
                    {t("back")}
                  </BackButton>
                )}
              </FooterSlot>
            </OverlayFooter>
          )}
        </OverlayBody>
      </OverlayCard>
    </OverlayFrame>
  );
}
